// Barre d'adresse du panneau Navigateur, logique pure (portee de browser-address.ts de l'ancienne
// app Pirouette). Le panneau n'est pas un navigateur personnel : la barre ne fait que naviguer.

export type LectureAdresse =
  | { ok: true; url: string; recherche?: boolean }
  | { ok: false; raison: "vide" | "invalide" };

/**
 * Requete vers une page de resultats. Bing, ni Google ni DuckDuckGo : depuis l'IP de centre de
 * donnees du navigateur sans tete, Google repond par un captcha et DuckDuckGo par sa page
 * d'accueil (constat de l'ancienne app, 25/08/2026).
 */
export function adresseDeRecherche(requete: string): string {
  return `https://www.bing.com/search?q=${encodeURIComponent(requete)}`;
}

/** `schema://` en tete. Seule la forme avec `//` compte : `javascript:alert(1)` n'en est pas. */
const SCHEMA = /^([a-z][a-z0-9+.-]*):\/\//i;
/** Hote plausible : deux etiquettes au moins, port facultatif ; ni deux-points ni parentheses. */
const HOTE = /^[^\s./?#@:()]+(\.[^\s./?#@:()]+)+(:\d+)?$/;

export function normaliserAdresse(brut: string): LectureAdresse {
  const valeur = brut.trim();
  if (!valeur) {
    return { ok: false, raison: "vide" };
  }
  const schema = SCHEMA.exec(valeur);
  if (schema) {
    const protocole = schema[1].toLowerCase();
    if (protocole !== "http" && protocole !== "https") {
      return { ok: false, raison: "invalide" };
    }
    const reste = valeur.slice(schema[0].length);
    if (!reste || /\s/.test(reste)) {
      return { ok: false, raison: "invalide" };
    }
    return { ok: true, url: valeur };
  }
  if (!/\s/.test(valeur)) {
    const hote = valeur.split(/[/?#]/)[0] ?? "";
    if (HOTE.test(hote)) {
      return { ok: true, url: `https://${valeur}` };
    }
  }
  return { ok: true, url: adresseDeRecherche(valeur), recherche: true };
}

export interface AffichageAdresse {
  securisee: boolean;
  hote: string;
  chemin: string;
}

export function formaterAdresse(url: string): AffichageAdresse {
  try {
    const u = new URL(url);
    const chemin = `${u.pathname === "/" ? "" : u.pathname}${u.search}${u.hash}`;
    return { securisee: u.protocol === "https:", hote: u.host, chemin };
  } catch {
    return { securisee: false, hote: url, chemin: "" };
  }
}

/** Hote en minuscules sans `www.`, "" si indeterminable. */
export function hoteDe(url: string): string {
  try {
    const hote = new URL(url).hostname.toLowerCase();
    return hote.startsWith("www.") ? hote.slice(4) : hote;
  } catch {
    return "";
  }
}

export function ancienneteRelative(a: number, maintenant: number): string {
  const secondes = Math.max(0, Math.floor((maintenant - a) / 1000));
  if (secondes < 45) {
    return "à l'instant";
  }
  const minutes = Math.round(secondes / 60);
  if (minutes < 60) {
    return `il y a ${minutes} min`;
  }
  return `il y a ${Math.round(minutes / 60)} h`;
}

export interface VisiteNavigateur {
  url: string;
  titre: string | null;
  a: number;
}

/** Ajoute une page vue ; une repetition consecutive complete le titre au lieu de doubler. */
export function ajouterVisite(
  visites: readonly VisiteNavigateur[],
  visite: VisiteNavigateur,
  max = 50
): VisiteNavigateur[] {
  const derniere = visites[visites.length - 1];
  if (derniere && derniere.url === visite.url) {
    if (visite.titre && visite.titre !== derniere.titre) {
      return [...visites.slice(0, -1), { ...derniere, titre: visite.titre }];
    }
    return visites as VisiteNavigateur[];
  }
  return [...visites, visite].slice(-max);
}
